import React, { Fragment, useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
  Typography,
} from "@material-ui/core";
import {
  Button,
  FormDataConsumer,
  required,
  SelectInput,
  SimpleForm,
  TextInput,
  useMutation,
  useNotify,
  useRefresh,
} from "react-admin";
import SaveIcon from "@material-ui/icons/Save";
import { useFormState } from "react-final-form";
import { StyledButton } from "../../../components/ListShowButton";
import { ERejectionReason, rejectionReasonList, remarkTypeFormatter } from "../const";

interface IRejectButton {
  id: number;
}

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  table: {
    marginTop: "10px",
    minWidth: 650,
    padding: "1rem",
  },
  form: {
    "& .MuiCardContent-root": {
      padding: "0 !important",
    },
  },
  cancelButton: {
    backgroundColor: "#eeeeee",
    color: "black",
  },
});

const ConfirmButton = ({ id }: IRejectButton) => {
  const refresh = useRefresh();
  const notify = useNotify();
  const { values } = useFormState();

  const [invalid, setInvalid] = useState<boolean>(true);

  // Determine remark based on selected rejection reason
  const remark =
    values.remark === ERejectionReason.other
      ? `Other: ${values.otherRemark}` // Add prefix for custom input
      : remarkTypeFormatter(values.remark); // Format predefined reason

  const [handleReject, { loading }] = useMutation(
    {
      type: "customUpdate",
      resource: `subcontractorAdjustmentPrices/${id}/reject`,
      payload: {
        data: {
          Remark: remark,
        },
      },
    },
    {
      onSuccess: ({ data }) => {
        notify(`Successfully reject adjustment price.`, "info");
        refresh();
      },
      onFailure: (error) =>
        notify(
          `Failed reject adjustment price, error: ${error.message}`,
          "warning",
        ),
    },
  );

  // enable/disable confirm button based on remark dropdown/other remark value
  useEffect(() => {
    const isRemarkValid = !!values.remark;
    const isOtherSelected = values.remark === ERejectionReason.other;
    const isOtherRemarkValid = !!values.otherRemark && values.otherRemark.trim() !== "";

    // Button is invalid if no remark selected or "Other" has empty text
    const isInvalid = !isRemarkValid || (isOtherSelected && !isOtherRemarkValid);

    setInvalid(isInvalid);
  }, [values]);

  return (
    <Button
      onClick={handleReject}
      color="primary"
      label={loading ? "Please wait..." : "Confirm"}
      disabled={loading || invalid}
      autoFocus
    >
      <SaveIcon />
    </Button>
  );
};

const RejectButton = ({ id }: IRejectButton) => {
  const [open, setOpen] = useState<boolean>(false);

  const classes = useStyles();

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Fragment>
      <StyledButton
        variant="outlined"
        onClick={handleOpenDialog}
        style={{ color: "#DA4040", borderColor: "#DA4040" }}
      >
        Reject
      </StyledButton>

      <SimpleForm toolbar={null} className={classes.form}>
        <Dialog
          open={open}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className={classes.dialog}
          maxWidth="md"
        >
          <DialogTitle id="alert-dialog-title">
            Reject The Adjustment Price
          </DialogTitle>
          <DialogContent>
            <DialogContentText color="initial">
              <Typography variant="body1" gutterBottom>
                Please provide a reason for rejecting this adjustment price.
              </Typography>
            </DialogContentText>

            <SelectInput
              source="remark"
              choices={rejectionReasonList}
              validate={required()}
            />

            <FormDataConsumer>
              {({ formData }) => formData.remark === ERejectionReason.other && (
                <TextInput
                  label="Remark Text"
                  source="otherRemark"
                  validate={required()}
                />
              )}
            </FormDataConsumer>
          </DialogContent>
          <DialogActions>
            <Button
              label="Back"
              color="secondary"
              onClick={handleClose}
              className={classes.cancelButton}
            />
            <ConfirmButton id={id} />
          </DialogActions>
        </Dialog>
      </SimpleForm>
    </Fragment>
  );
};

export default RejectButton;
